import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
  Legend,
} from 'recharts';
import type { VolumeMetrics } from '../types';
import { MUSCLE_GROUP_LABELS } from '../types';
import { volumeStatusColor, volumeStatusLabel } from '../utils/helpers';

interface Props {
  volume: VolumeMetrics;
}

export default function VolumeChart({ volume }: Props) {
  const data = useMemo(
    () =>
      volume.muscleVolumes
        .filter((mv) => mv.directSets > 0 || mv.indirectSets > 0 || mv.mev > 0)
        .map((mv) => ({
          name: MUSCLE_GROUP_LABELS[mv.muscle],
          direct: mv.directSets,
          indirect: mv.indirectSets,
          total: mv.totalSets,
          mev: mv.mev,
          mav: mv.mav,
          mrv: mv.mrv,
          status: mv.status,
          color: volumeStatusColor(mv.status),
        })),
    [volume]
  );

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
      <div className="bg-white dark:bg-slate-800 shadow-lg rounded-lg p-3 border border-slate-200 dark:border-slate-700 text-xs">
        <p className="font-bold mb-1">{label}</p>
        <p>Bezpośrednio: <span className="font-mono">{d.direct}</span> serii</p>
        <p>Pośrednio: <span className="font-mono">{d.indirect}</span> serii</p>
        <p className="font-semibold mt-1">Razem: <span className="font-mono">{d.total}</span> serii</p>
        <div className="mt-1.5 pt-1.5 border-t border-slate-100 dark:border-slate-700 space-y-0.5">
          <p className="text-slate-400">MEV: {d.mev} · MAV: {d.mav} · MRV: {d.mrv}</p>
          <p style={{ color: d.color }} className="font-semibold capitalize">
            {volumeStatusLabel(d.status)}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
      <h3 className="text-sm font-bold mb-1">Tygodniowa objętość na grupę mięśniową</h3>
      <p className="text-[10px] text-slate-400 mb-3">
        Serie na mięsień - kolorowane według statusu MEV/MAV/MRV
      </p>

      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
            <XAxis
              dataKey="name"
              tick={{ fontSize: 9, fill: '#94a3b8' }}
              angle={-45}
              textAnchor="end"
              height={70}
              interval={0}
            />
            <YAxis
              tick={{ fontSize: 9, fill: '#94a3b8' }}
              label={{ value: 'Serie', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#94a3b8' } }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="total" radius={[4, 4, 0, 0]} maxBarSize={28}>
              {data.map((entry, idx) => (
                <Cell key={idx} fill={entry.color} fillOpacity={0.85} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Summary stats */}
      <div className="flex items-center gap-4 mt-3 text-[10px] text-slate-500">
        <span>Razem: <b className="text-slate-700 dark:text-slate-200">{volume.totalSets}</b> serii</span>
        <span>Wielostawowych: <b className="text-slate-700 dark:text-slate-200">{Math.round(volume.compoundRatio * 100)}%</b></span>
        <span>Objętość obciążenia: <b className="text-slate-700 dark:text-slate-200">{volume.volumeLoadScore}</b></span>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-2">
        {[
          { label: 'Poniżej MEV', color: '#6366f1' },
          { label: 'Na MEV', color: '#3b82f6' },
          { label: 'Optymalnie', color: '#22c55e' },
          { label: 'Blisko MRV', color: '#f59e0b' },
          { label: 'Nad MRV', color: '#ef4444' },
        ].map((l) => (
          <div key={l.label} className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: l.color }} />
            <span className="text-[10px] text-slate-500">{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
